import React from 'react';
import { BillingMethod, CampaignFormStepData, CampaignFields, CampaignStep, UpdateCampaignRequest } from 'types';
import { List, ListItem, Typography } from '@mui/material';
import { CampaignStepState, OrderedSteps, StepDescriptor, StepIndices, StepMap } from 'types/Campaign';
import invariant from 'tiny-invariant';

export const BUTTON_HEIGHT_CONSTANT = 7; // theme.spacing units
export const MIN_PRICE_ENGAGEMENT = 5;
export const MIN_PRICE_CLICK = 3;

export const defaultUpdateRequestBody: Omit<
  UpdateCampaignRequest,
  | 'name'
  | 'budget'
  | 'targetingGuidance'
  | 'targetingGuidanceConfig'
  | 'billingMethod'
  | 'websites'
  | 'effectiveCostPerUnit'
  | 'startDate'
  | 'endDate'
  | 'stoppedAt'
> = {
  billingAddressSalutation: 'none',
  billingAddressFirstName: '',
  billingAddressLastName: '',
  billingAddressCompanyName: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingAddressCountry: '',
  billingAddressPostcode: '',
  billingAddressCity: '',
  billingAddressEmail: '',
  billingAddressPhone: '',
  contactAddressSalutation: 'none',
  contactAddressFirstName: '',
  contactAddressLastName: '',
  contactAddressCompanyName: '',
  contactAddressLine1: '',
  contactAddressLine2: '',
  contactAddressCountry: '',
  contactAddressPostcode: '',
  contactAddressCity: '',
  contactAddressEmail: '',
  contactAddressPhone: '',
  maxUnitPrice: null,
  ga4PropertyId: '',
  targetingGuidanceMode: 'none',
  targetingGuidanceFormat: 'json',
};

/**
 * Description of steps as a source of truth.
 * This collection of step descriptors is transposed into any required representations.
 */
const steps = [
  {
    step: CampaignStep.Basic,
    title: 'Los geht’s',
    subtitle: 'Schnell und einfach deine Werbebanner veröffentlichen',
    label: 'Basisinfos',
    fields: [CampaignFields.name, CampaignFields.campaignPeriod, CampaignFields.budget, CampaignFields.websites],
    state: CampaignStepState.Default,
  },
  {
    step: CampaignStep.Creatives,
    title: 'Creatives',
    subtitle: 'Lade Bilder und Videos für die Kampagne hoch',
    label: 'Creatives',
    fields: [CampaignFields.websites],
    state: CampaignStepState.Default,
  },
  {
    step: CampaignStep.Targeting,
    title: 'Zielgruppen',
    subtitle: 'Optimierte Zielgruppen für mehr Sichtbarkeit',
    label: 'Zielgruppen',
    fields: [CampaignFields.targetingGuidance],
    state: CampaignStepState.Default,
  },
  {
    step: CampaignStep.KPI,
    title: 'Preismodell',
    subtitle: 'Wie soll die Kampagne berechnet werden?',
    label: 'Preismodell',
    fields: [CampaignFields.billingMethod, CampaignFields.maxUnitPrice],
    state: CampaignStepState.Default,
  },
  {
    step: CampaignStep.GA,
    title: 'Google Analytics 4 verbinden',
    subtitle: 'User Engagement deiner Kampagne messen',
    label: 'Google Analytics',
    fields: [CampaignFields.ga4PropertyId],
    state: CampaignStepState.Default,
  },
  {
    step: CampaignStep.Complete,
    title: 'Gleich geschafft!',
    subtitle: 'Kontrolliere deine Angaben',
    label: 'Abschliessen',
    fields: [],
    state: CampaignStepState.Default,
  },
] as const;

const buildStepMap = <StepDescriptors extends readonly StepDescriptor<any, any>[]>(stepDescs: StepDescriptors) =>
  stepDescs.reduce(
    (acc, desc, i) => {
      const step = desc.step as CampaignStep;
      invariant(!(step in acc.campaignSteps), `Non unique step ${step}`);

      (acc.orderedSteps as CampaignStep[]).push(step);
      (acc.stepIndices as Record<CampaignStep, number>)[step] = i;
      (acc.campaignSteps as Record<CampaignStep, CampaignFormStepData>)[step] = {
        ...desc,
        state: CampaignStepState.Default,
      };

      return acc;
    },
    {
      campaignSteps: {} as StepMap<StepDescriptors>,
      orderedSteps: [] as OrderedSteps<StepDescriptors>,
      stepIndices: {} as StepIndices<StepDescriptors>,
    }
  );

export const { campaignSteps, orderedSteps, stepIndices } = buildStepMap(steps);

// KPIs

const listProps = { sx: { listStyleType: 'disc', mb: 1, fontSize: 14 } };
const listItemProps = {
  disablePadding: true,
  sx: { display: 'list-item', ml: 5, maxWidth: 'fit-content' },
};

export const texts = {
  [BillingMethod.ENGAGEMENT]: {
    minPrice: `Ein Preis unter CHF ${MIN_PRICE_ENGAGEMENT} könnte die Performance einschränken.`,
    cardTitle: 'Max. Preis pro Engagement festlegen (Optional)',
    inputLabel: 'Max. Preis pro Engagement (Optional)',
    selectHelper: 'Bei diesem Preismodell ist ein Google Analytics 4-Konto erforderlich. Siehe nächster Schritt.',
  },
  [BillingMethod.CLICK]: {
    minPrice: `Ein Preis unter CHF ${MIN_PRICE_CLICK} könnte die Performance einschränken.`,
    cardTitle: 'Max. Preis pro Klick festlegen (Optional)',
    inputLabel: 'Max. Preis pro Klick (Optional)',
    selectHelper: '',
  },
};

export const infoMessagesTop = {
  [BillingMethod.ENGAGEMENT]: [
    {
      title: 'Was zählt als Engagement?',
      message: (
        <>
          <List {...listProps}>
            <ListItem {...listItemProps}>
              Ein <b>Conversion Event</b> wurde ausgelöst
            </ListItem>
            <ListItem {...listItemProps}>
              oder der User ist für min. <b>10 Sekunden auf deiner Webseite</b>
            </ListItem>
            <ListItem {...listItemProps}>
              oder es gab mindestens 2 <b>Pageviews</b>
            </ListItem>
          </List>

          <Typography variant="body2">
            Zur Messung ist ein Google Analytics 4-Konto erforderlich. Siehe nächster Schritt.
          </Typography>
        </>
      ),
    },
    {
      title: 'Empfehlung',
      message: (
        <Typography variant="body2">
          Dieses Preismodell wird empfohlen, da nur jene User verrechnet werden, welche effektiv Interesse zeigen.
        </Typography>
      ),
    },
  ],
  [BillingMethod.CLICK]: [
    {
      title: 'Was wird als Klick gezählt?',
      message: <Typography variant="body2">Die Kampagne wird pro Klick auf die Banner berechnet.</Typography>,
    },
    {
      title: 'Empfehlung',
      message: (
        <List {...listProps}>
          <ListItem {...listItemProps}>Wenn kein Google Analytics 4 (GA4) Konto vorhanden ist.</ListItem>
          <ListItem {...listItemProps}>
            Wenn die Verbindung zwischen Alloy und GA4 nicht gewünscht wird oder wenn die Verknüpfung nicht
            funktioniert.
          </ListItem>
        </List>
      ),
    },
  ],
};

export const infoMessagesBottom = {
  [BillingMethod.ENGAGEMENT]: [
    {
      title: 'Alloy optimiert deinen Preis',
      message: (
        <Typography variant="body2">
          Alloy sucht stets den günstigsten Preis – unabhängig davon, ob du einen Maximalpreis pro Engagement festlegst.
          Mit einem Maximalpreis zahlst du nie mehr als diesen Betrag pro Engagement.
          <br />
          <br />
          Falls die Engagements zu niedrig ausfallen (z. B. durch einen zu niedrigen Maximalpreis), wirst du per E-Mail
          informiert – inklusive Tipps zur Verbesserung der Performance.
        </Typography>
      ),
    },
    {
      title: 'Empfehlung',
      message: (
        <Typography variant="body2">
          Lasse den Maximalpreis zu Beginn offen – du kannst ihn jederzeit anpassen. Alloy findet stets den günstigsten
          Preis.
        </Typography>
      ),
    },
  ],
  [BillingMethod.CLICK]: [
    {
      title: 'Alloy optimiert deinen Preis',
      message: (
        <Typography variant="body2">
          Alloy sucht stets den günstigsten Preis – unabhängig davon, ob du einen Maximalpreis pro Klick festlegst. Mit
          einem Maximalpreis zahlst du nie mehr als diesen Betrag pro Klick.
          <br />
          <br />
          Falls die Klicks zu niedrig ausfallen (z. B. durch einen zu niedrigen Maximalpreis), wirst du per E-Mail
          informiert – inklusive Tipps zur Verbesserung der Performance.
        </Typography>
      ),
    },
    {
      title: 'Empfehlung',
      message: (
        <Typography variant="body2">
          Lasse den Maximalpreis zu Beginn offen – du kannst ihn jederzeit anpassen. Alloy findet stets den günstigsten
          Preis.
        </Typography>
      ),
    },
  ],
};

export const websiteUrlIndexAttrName = 'data-website-url-index';

export const getWebsiteUrlTextbox = (index: number) =>
  document.querySelector<HTMLInputElement>(`input[${websiteUrlIndexAttrName}="${index}"]`);

export const focusWebsiteUrl = async (index: number) => {
  return new Promise<void>((resolve) =>
    setTimeout(() => {
      const textbox = getWebsiteUrlTextbox(index);
      if (textbox) {
        textbox.focus();
        textbox.select();
      }
      resolve();
    }, 10)
  );
};
